import React from 'react';
import FreshnessIndicator from '@/shared/components/ui/FreshnessIndicator';
import { getLinkedIdentities, startGoogleLinkFlow } from '@/features/system/auth/api/authApi';
import { notifyAuthDisconnected } from '@/lib/auth/authDisconnect';

type LinkStatus = 'linked' | 'not_linked' | 'unknown';

// Reasons that mean "the user's BFF session is genuinely no longer usable".
// In that state the OIDC link/start flow CANNOT succeed (the BFF needs a
// live appJwt to mint the provider redirect), so we pre-empt the click in
// the UI instead of letting the user press a button and get bounced to
// /login via the global session-liveness interceptor.
//
// CRITICAL: keep this set in lock-step with `SESSION_DEAD_REASONS` in
// `bff-fe/server/bff/lib/session-contract.mjs`. Anything outside this set
// is an INFRASTRUCTURE signal — rendering "Sign In Again" for an infra
// reason creates the kick-out loop that this panel is explicitly designed
// to prevent (a misconfigured IDENTITY_BACKEND_URL would 401 the user
// out forever otherwise).
const SESSION_DEAD_REASONS = new Set([
  'no_session',
  'session_absent',
  'session_expired',
  'missing_session_token',
  'refresh_rejected',
  'invalid_refresh_shape',
  'session_not_found',
  'no_refresh_token',
]);

// Reasons that mean "the identity backend is unreachable / misconfigured /
// rejecting valid tokens". User's session is fine; they should retry, not
// re-authenticate. Used to gate the panel's CTA into a "Diagnose & Retry"
// affordance instead of a logout-triggering "Sign In Again".
const INFRA_DEGRADED_REASONS = new Set([
  'upstream_not_found',
  'upstream_unauthorized',
  'upstream_timeout',
  'upstream_error',
  'forbidden',
]);

// Reasons that mean "the federated identity capability is intentionally
// OFF in this environment" (e.g. dev backend with PAC_IAM_IDENTITY_ENABLED
// unset). Distinct from infra-degraded because a retry will NEVER succeed
// — the panel must render a calm, neutral "Not Enabled" message instead
// of a red alarm with a retry CTA. Keep in lock-step with
// FEATURE_DISABLED_REASONS in `bff-fe/server/bff/lib/session-contract.mjs`.
const FEATURE_DISABLED_REASONS = new Set([
  'feature_not_enabled',
]);

// Human-readable degradation hints. Keys must mirror the BFF
// `DegradedReason` codes from `bff-fe/server/bff/lib/session-contract.mjs`.
// Rendered to the user in place of "STATUS UNKNOWN" so they understand WHY
// the panel cannot read the link state — without ever forcing a logout.
const DEGRADED_HINTS: Record<string, string> = {
  session_absent:        'Sign in to view link status. You can still start explicit linking below.',
  session_expired:       'Your sign-in expired. Refresh the page to view link status.',
  missing_session_token: 'Your sign-in is incomplete. Refresh the page to view link status.',
  refresh_rejected:      'Your sign-in could not be refreshed. Sign in again to view link status.',
  upstream_not_found:    'Identity service is not reachable at the configured host. Operators have been notified.',
  upstream_unauthorized: 'Identity service rejected a freshly-issued session token. This is a service issue, not a sign-in issue.',
  upstream_error:        'Identity service is temporarily unavailable. Try again in a moment.',
  upstream_timeout:      'Identity service did not respond in time. Try refresh.',
  forbidden:             'You do not have permission to view this status.',
  feature_not_enabled:   'Federated identity linking is not enabled in this environment. This capability is provisioned per-environment by platform operators.',
};

export default function IdentityLinkingPanel() {
  const [linkBusy, setLinkBusy] = React.useState(false);
  const [linkMsg, setLinkMsg] = React.useState<string | null>(null);
  const [linkErr, setLinkErr] = React.useState<string | null>(null);
  const [statusBusy, setStatusBusy] = React.useState(true);
  const [statusValue, setStatusValue] = React.useState<LinkStatus>('unknown');
  const [statusDetail, setStatusDetail] = React.useState('Checking linked identity state...');
  const [statusCheckedAt, setStatusCheckedAt] = React.useState<number | null>(null);
  const [degradedReason, setDegradedReason] = React.useState<string | null>(null);

  const refreshGoogleStatus = React.useCallback(async () => {
    setStatusBusy(true);
    try {
      const result = await getLinkedIdentities();
      const now = result?.fetched_at ?? Date.now();
      setStatusCheckedAt(now);
      if (!result) {
        setStatusValue('unknown');
        setDegradedReason('upstream_error');
        setStatusDetail(DEGRADED_HINTS.upstream_error);
        return;
      }
      if (result.degraded) {
        // Soft-endpoint degraded payload — show explicit hint, never log out.
        const reason = result.reason ?? 'upstream_error';
        setStatusValue('unknown');
        setDegradedReason(reason);
        setStatusDetail(DEGRADED_HINTS[reason] ?? DEGRADED_HINTS.upstream_error);
        return;
      }
      setDegradedReason(null);
      if (result.google_linked) {
        setStatusValue('linked');
        setStatusDetail(result.google_email
          ? `Linked to Google (${result.google_email}).`
          : 'Google identity is linked to this PAC user.');
      } else {
        setStatusValue('not_linked');
        setStatusDetail('No linked Google identity detected yet.');
      }
    } catch {
      // The fetch itself shouldn't throw — getLinkedIdentities catches —
      // but be defensive.
      setStatusCheckedAt(Date.now());
      setStatusValue('unknown');
      setDegradedReason('upstream_error');
      setStatusDetail(DEGRADED_HINTS.upstream_error);
    } finally {
      setStatusBusy(false);
    }
  }, []);

  // Redirect callback from backend: /?pac_link_ok=<provider>
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const provider = params.get('pac_link_ok');
    if (!provider) return;
    window.history.replaceState(null, '', window.location.pathname + window.location.hash);
    const name = provider.charAt(0).toUpperCase() + provider.slice(1);
    setLinkMsg(`${name} account linked successfully.`);
    setLinkErr(null);
    void refreshGoogleStatus();
  }, [refreshGoogleStatus]);

  React.useEffect(() => {
    void refreshGoogleStatus();
  }, [refreshGoogleStatus]);

  const statusClass = statusValue === 'linked'
    ? 'border-emerald-500/30 text-emerald-300 bg-emerald-950/30'
    : statusValue === 'not_linked'
      ? 'border-amber-500/30 text-amber-200 bg-amber-950/30'
      : degradedReason && FEATURE_DISABLED_REASONS.has(degradedReason)
        ? // Feature intentionally OFF — neutral slate, never amber alarm.
          'border-slate-500/30 text-slate-300 bg-slate-900/40'
        : 'border-neutral-500/30 text-neutral-300 bg-neutral-900/40';

  // True when the panel knows the BFF session is gone — used to pre-empt
  // the Link Google click so the user is offered a clean re-auth path
  // instead of being bounced through the global interceptor.
  const sessionDead = !!degradedReason && SESSION_DEAD_REASONS.has(degradedReason);
  // True when the panel knows the identity backend is the failure source
  // (misconfigured, unreachable, or rejecting fresh tokens). NEVER trigger
  // logout for these — that creates an infinite kick-out loop.
  const infraDegraded = !!degradedReason && INFRA_DEGRADED_REASONS.has(degradedReason);
  // True when the capability is intentionally OFF in this environment.
  // Render a neutral informational state — no retry, no alarm.
  const featureDisabled = !!degradedReason && FEATURE_DISABLED_REASONS.has(degradedReason);

  return (
    <div className="space-y-3">
      <div className="text-xs text-neutral-500">
        Link your PAC account to a Google identity using explicit authenticated consent.
        This is an account-level security action and is not tied to a business feature module.
      </div>

      <div className="rounded-xl pac-glass border border-emerald-500/20 p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="text-sm font-semibold tracking-wide text-neutral-200">Federated Identity Linking</p>
            <p className="text-xs text-neutral-500 mt-1">
              Primary provider: Google OAuth2 / OIDC.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <FreshnessIndicator
              lastFetchedAt={statusCheckedAt}
              isFetching={statusBusy}
              label={statusCheckedAt ? (Date.now() - statusCheckedAt <= 30_000 ? 'LIVE' : 'STALE') : 'UNKNOWN'}
            />
            <span className={`text-[11px] px-2 py-1 rounded border ${statusClass} ${statusBusy ? 'animate-pulse' : ''}`}
                  title={degradedReason ? `Degraded: ${degradedReason}` : undefined}>
              {statusBusy
                ? 'CHECKING'
                : statusValue === 'linked'
                  ? 'GOOGLE LINKED'
                  : statusValue === 'not_linked'
                    ? 'NOT LINKED'
                    : featureDisabled
                      ? 'NOT ENABLED'
                      : (degradedReason ? `DEGRADED · ${degradedReason.toUpperCase()}` : 'STATUS UNKNOWN')}
            </span>
          </div>
        </div>

        <p className="mt-3 text-xs text-neutral-400">{statusDetail}</p>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          {sessionDead ? (
            // Session is genuinely dead — clicking Link Google would 401 at
            // /auth/oidc/google/link/start and trigger the global
            // session-liveness interceptor (correct, but a jarring UX). We
            // pre-empt with an honest "Sign In Again" action that goes
            // through the same notifier so the SPA performs a clean
            // re-auth flow without surprise.
            <button
              type="button"
              onClick={() => {
                notifyAuthDisconnected({
                  reason: 'unauthenticated',
                  url: '/bff/auth/linked-identities',
                  status: 401,
                });
              }}
              className="pac-glass-btn rounded px-3 py-1.5 text-xs font-semibold text-amber-300 border-amber-500/40 hover:border-amber-400/70"
            >
              Sign In Again to Link Google
            </button>
          ) : featureDisabled ? (
            // Federated identity is intentionally OFF in this environment.
            // Render NO action button — a retry would 404 forever, and a
            // sign-in would not enable the feature. A neutral inline note
            // suffices; the descriptive `statusDetail` already explains it.
            <span className="text-[11px] text-slate-400 italic">
              Linking actions are disabled in this environment.
            </span>
          ) : infraDegraded ? (
            // Identity backend is the failure source — DO NOT offer a
            // sign-in-again button: signing in again will not fix a
            // misconfigured/unreachable upstream and would just kick the
            // user out repeatedly. Offer a plain retry instead.
            <button
              type="button"
              disabled={statusBusy}
              onClick={() => { void refreshGoogleStatus(); }}
              className="pac-glass-btn rounded px-3 py-1.5 text-xs font-semibold text-amber-300 border-amber-500/40 hover:border-amber-400/70 disabled:opacity-50"
              title={degradedReason ? `Reason: ${degradedReason}` : undefined}
            >
              {statusBusy ? 'Retrying...' : 'Retry Identity Service'}
            </button>
          ) : (
            <button
              type="button"
              disabled={linkBusy}
              onClick={async () => {
                setLinkBusy(true);
                setLinkErr(null);
                setLinkMsg('Opening Google consent...');
                try {
                  await startGoogleLinkFlow();
                } catch (err: any) {
                  setLinkErr(String(err?.message || err || 'Could not start account linking.'));
                  setLinkMsg(null);
                  void refreshGoogleStatus();
                } finally {
                  setLinkBusy(false);
                }
              }}
              className="pac-glass-btn rounded px-3 py-1.5 text-xs font-semibold text-emerald-300 border-emerald-500/30 hover:border-emerald-500/60 disabled:opacity-50"
            >
              {linkBusy ? 'Linking...' : 'Link Google Account'}
            </button>
          )}

          <button
            type="button"
            disabled={statusBusy}
            onClick={() => { void refreshGoogleStatus(); }}
            className="pac-glass-btn rounded px-3 py-1.5 text-xs font-semibold text-cyan-300 border-cyan-500/30 hover:border-cyan-500/60 disabled:opacity-50"
          >
            {statusBusy ? 'Refreshing...' : 'Refresh Status'}
          </button>

          <span className="text-[11px] text-neutral-500">
            No implicit email auto-link. Existing PAC account remains canonical.
          </span>
        </div>

        {linkMsg && !linkErr && (
          <p className="mt-3 text-xs text-emerald-300">{linkMsg}</p>
        )}
        {linkErr && (
          <p className="mt-3 text-xs text-red-300">{linkErr}</p>
        )}
      </div>
    </div>
  );
}